import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from './Loading';
import ProductRow from './ProductRow';
import {
  MAX_CONTENT_WIDTH,
  SPACING_TIGHT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
  SPACING_PAGE,
} from '../constants/layout';
import { ProductsContext } from '../contexts/ProductsContext';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

const isProductActive = product => {
  const deactivatedDate = parseDate(product?.deactivatedDate);
  if (!deactivatedDate) return true;
  const today = new Date();
  return today <= deactivatedDate;
};

export default function CategoryDetail() {
  const { id } = useParams();
  const { products, imageItems, getCoverImage } = useContext(ProductsContext);
  const [category, setCategory] = useState(null);
  const [loadingCategory, setLoadingCategory] = useState(true);
  const [loadedThumbnails, setLoadedThumbnails] = useState(new Set());

  useEffect(() => {
    let isMounted = true;

    async function fetchCategory() {
      if (!id) return;
      try {
        const ref = doc(db, 'categories', id);
        const snap = await getDoc(ref);
        if (!isMounted) return;
        if (snap.exists()) {
          setCategory({ id: snap.id, ...snap.data() });
        } else {
          setCategory(null);
        }
      } finally {
        if (isMounted) {
          setLoadingCategory(false);
        }
      }
    }

    fetchCategory();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    setLoadedThumbnails(new Set());
  }, [id]);

  useEffect(() => {
    if (category?.title) {
      document.title = category.title;
      return () => {
        document.title = 'Catalog';
      };
    }
    return undefined;
  }, [category?.title]);

  const categoryProductIds = useMemo(
    () => (Array.isArray(category?.itemIDs) ? category.itemIDs : []),
    [category?.itemIDs]
  );

  const categoryProducts = useMemo(() => {
    return products
      .filter(prod => categoryProductIds.includes(prod.id))
      .filter(isProductActive)
      .sort((a, b) => a.productName.localeCompare(b.productName));
  }, [products, categoryProductIds]);

  const handleThumbnailLoad = productId => {
    setLoadedThumbnails(prev => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  if (loadingCategory) {
    return <Loading />;
  }

  if (!category) {
    return (
      <div className="category-detail-container">
        <div className="category-header">
          <h1 className="category-title">Category not found</h1>
          <p className="category-description">
            We couldn't find the category you're looking for.
          </p>
        </div>
        <div className="back-link-wrapper">
          <Link to="/" className="back-link">
            ← Back to catalog
          </Link>
        </div>
        <style jsx>{`
          .category-detail-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .category-header {
            margin-bottom: ${SPACING_SECTION};
          }

          .category-title {
            font-size: 2rem;
            font-family: var(--font-family-sans);
            font-weight: 800;
            margin: 0;
            text-transform: uppercase;
          }

          .category-description {
            color: var(--color-text-secondary);
            margin-top: ${SPACING_BUTTON};
          }

          .back-link-wrapper {
            margin-top: ${SPACING_GROUP};
          }

          .back-link {
            color: var(--color-link);
            text-decoration: none;
            font-weight: 500;
          }

          .back-link:hover,
          .back-link:focus {
            text-decoration: underline;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="category-detail-container">
      <div className="category-header">
        <h1 className="category-title">{category.title}</h1>
        {typeof category.subtitle === 'string' && category.subtitle.trim() ? (
          <p className="category-subtitle">{category.subtitle}</p>
        ) : null}
        {category.description ? (
          <p className="category-description">{category.description}</p>
        ) : null}
      </div>

      <div className="products-wrapper">
        {categoryProducts.length === 0 ? (
          <p className="empty-state">No products are currently available in this category.</p>
        ) : (
          categoryProducts.map(product => (
            <ProductRow
              key={product.id}
              product={product}
              coverImage={getCoverImage(product)}
              isLoaded={loadedThumbnails.has(product.id)}
              onImageLoad={handleThumbnailLoad}
            />
          ))
        )}
      </div>

      <div className="back-link-wrapper">
        <Link to="/" className="back-link">
          ← Back to catalog
        </Link>
      </div>

      <style jsx>{`
        .category-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
        }

        .category-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .category-title {
          font-size: 2.25rem;
          font-family: var(--font-family-sans);
          font-weight: 800;
          margin: 0;
          text-transform: uppercase;
        }

        .category-subtitle {
          margin: ${SPACING_TIGHT} 0 0;
          color: var(--color-text-secondary);
        }

        .category-description {
          color: var(--color-text-secondary);
          margin-top: ${SPACING_BUTTON};
          line-height: 1.6;
        }

        .products-wrapper {
          margin-bottom: ${SPACING_HERO};
        }

        .empty-state {
          color: var(--color-text-muted);
        }

        .back-link-wrapper {
          margin-top: ${SPACING_GROUP};
        }

        .back-link {
          color: var(--color-link);
          text-decoration: none;
          font-weight: 500;
        }

        .back-link:hover,
        .back-link:focus {
          text-decoration: underline;
        }

        @media (max-width: 600px) {
          .category-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
    </div>
  );
}
